/**
 * AI Enrichment Hub
 * 
 * Unified dashboard for all AI enrichment features:
 * - Enrichment: Single-page wizard for selection, processing, and approval
 * - Pricing Rules: Configure SKU/category pricing rules  
 * - Tag Schema: Manage approved tag taxonomy
 * - Settings: Auto-trigger and enrichment settings
 */

import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Spinner, Tab, Nav, Card, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faRobot,
    faTags,
    faDollarSign,
    faCog
} from '@fortawesome/free-solid-svg-icons';
import { FirebaseAuthContext } from 'context/FirebaseAuthContext.js';
import { db } from 'config/firebase.js';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';

import { toast } from 'react-toastify';
import PageHeader from 'components/common/PageHeader';

// New Unified Wizard
import UnifiedEnrichmentWizard from './UnifiedEnrichmentWizard';

// Import Phase 3 components (Settings/Config)
import TagSchemaManager from './TagSchemaManager';
import PricingRulesConfig from 'components/settings/PricingRulesConfig';

import './AIEnrichmentHub.css';

const AIEnrichmentHub = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user, authLoading } = useContext(FirebaseAuthContext);

    // Get initial tab from URL or default to 'wizard'
    const initialTab = searchParams.get('tab') || 'wizard';
    const [activeTab, setActiveTab] = useState(initialTab);

    // Redirect if not authenticated
    useEffect(() => {
        if (authLoading) return;
        if (!user) return navigate('/');
    }, [authLoading, user, navigate]);

    // Handle tab change - update URL
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setSearchParams({ tab });
    };

    const tabs = [
        {
            key: 'wizard',
            title: 'Enrichment',
            icon: faRobot
        },
        {
            key: 'pricing',
            title: 'Pricing Rules',
            icon: faDollarSign
        },
        {
            key: 'tags',
            title: 'Tag Schema',
            icon: faTags
        },
        {
            key: 'settings',
            title: 'Settings',
            icon: faCog
        }
    ];

    return (
        <div className="ai-enrichment-hub">
            <PageHeader
                title="AI Enrichment Hub"
                description="Manage AI-powered product data enrichment"
                className="mb-3"
            />

            <Tab.Container activeKey={activeTab} onSelect={handleTabChange}>
                <Card className="mb-3">
                    <Card.Header className="p-0 bg-light">
                        <Nav variant="tabs" className="enrichment-nav">
                            {tabs.map(tab => (
                                <Nav.Item key={tab.key}>
                                    <Nav.Link
                                        eventKey={tab.key}
                                        className="d-flex align-items-center gap-2 px-4 py-3"
                                    >
                                        <FontAwesomeIcon icon={tab.icon} />
                                        <span>{tab.title}</span>
                                    </Nav.Link>
                                </Nav.Item>
                            ))}
                        </Nav>
                    </Card.Header>
                </Card>

                <Tab.Content>
                    {/* Unified Enrichment Wizard */}
                    <Tab.Pane eventKey="wizard">
                        {activeTab === 'wizard' && <UnifiedEnrichmentWizard />}
                    </Tab.Pane>

                    {/* Pricing Rules Tab */}
                    <Tab.Pane eventKey="pricing">
                        {activeTab === 'pricing' && <PricingRulesConfig />}
                    </Tab.Pane>

                    {/* Tag Schema Tab */}
                    <Tab.Pane eventKey="tags">
                        {activeTab === 'tags' && <TagSchemaManager />}
                    </Tab.Pane>

                    {/* Settings Tab */}
                    <Tab.Pane eventKey="settings">
                        {activeTab === 'settings' && <EnrichmentSettings />}
                    </Tab.Pane>
                </Tab.Content>
            </Tab.Container>
        </div>
    );
};

/**
 * Enrichment Settings Component
 */
const EnrichmentSettings = () => {
    const [settings, setSettings] = useState({
        autoEnrichNewProducts: false,
        autoEnrichModes: ['metadata', 'title', 'description', 'seo'],
        autoEnrichMaxPerHour: 50,
        autoApproveHighConfidence: true,
        confidenceThreshold: 90
    });
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);

    // Load settings
    useEffect(() => {
        const loadSettings = async () => {
            try {
                const settingsRef = doc(db, 'productCatalog/topicz/enrichment/settings');
                const snap = await getDoc(settingsRef);
                if (snap.exists()) {
                    setSettings({ ...settings, ...snap.data() });
                }
            } catch (error) {
                console.error('Error loading settings:', error);
            } finally {
                setLoading(false);
            }
        };
        loadSettings();
    }, []);

    const handleSave = async () => {
        setSaving(true);
        try {
            const settingsRef = doc(db, 'productCatalog/topicz/enrichment/settings');
            await setDoc(settingsRef, settings, { merge: true });
            // Also save timestamp
            await updateDoc(settingsRef, { updatedAt: new Date() });

            toast.success('Settings saved successfully!');
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Error saving settings');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="p-5 text-center"><Spinner animation="border" /></div>;

    return (
        <Card>
            <Card.Header>
                <h5 className="mb-0">
                    <FontAwesomeIcon icon={faCog} className="me-2" />
                    Enrichment Settings
                </h5>
            </Card.Header>
            <Card.Body>
                <Row>
                    <Col md={6}>
                        <h6>Auto-Trigger Settings</h6>

                        <div className="form-check mb-3">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id="autoEnrich"
                                checked={settings.autoEnrichNewProducts}
                                onChange={(e) => setSettings({
                                    ...settings,
                                    autoEnrichNewProducts: e.target.checked
                                })}
                            />
                            <label className="form-check-label" htmlFor="autoEnrich">
                                Auto-enrich new products from iBMi
                            </label>
                            <small className="d-block text-muted">
                                Automatically queue enrichment when products are ingested
                            </small>
                        </div>

                        <div className="mb-3">
                            <label className="form-label">Max Products per Hour</label>
                            <input
                                type="number"
                                className="form-control"
                                value={settings.autoEnrichMaxPerHour}
                                onChange={(e) => setSettings({
                                    ...settings,
                                    autoEnrichMaxPerHour: parseInt(e.target.value) || 50
                                })}
                                min="1"
                                max="500"
                            />
                            <small className="text-muted">Rate limit for auto-triggered enrichment</small>
                        </div>

                        <h6 className="mt-4">Auto-Trigger Modes</h6>
                        {['metadata', 'title', 'description', 'seo', 'pricing', 'weight'].map(mode => (
                            <div className="form-check" key={mode}>
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id={`mode-${mode}`}
                                    checked={settings.autoEnrichModes.includes(mode)}
                                    onChange={(e) => {
                                        const modes = e.target.checked
                                            ? [...settings.autoEnrichModes, mode]
                                            : settings.autoEnrichModes.filter(m => m !== mode);
                                        setSettings({ ...settings, autoEnrichModes: modes });
                                    }}
                                />
                                <label className="form-check-label text-capitalize" htmlFor={`mode-${mode}`}>
                                    {mode}
                                </label>
                            </div>
                        ))}
                    </Col>

                    <Col md={6}>
                        <h6>Approval Settings</h6>

                        <div className="form-check mb-3">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id="autoApprove"
                                checked={settings.autoApproveHighConfidence}
                                onChange={(e) => setSettings({
                                    ...settings,
                                    autoApproveHighConfidence: e.target.checked
                                })}
                            />
                            <label className="form-check-label" htmlFor="autoApprove">
                                Auto-approve high confidence results
                            </label>
                            <small className="d-block text-muted">
                                Skip review for results above confidence threshold
                            </small>
                        </div>

                        <div className="mb-3">
                            <label className="form-label">
                                Confidence Threshold: {settings.confidenceThreshold}%
                            </label>
                            <input
                                type="range"
                                className="form-range"
                                value={settings.confidenceThreshold}
                                onChange={(e) => setSettings({
                                    ...settings,
                                    confidenceThreshold: parseInt(e.target.value)
                                })}
                                min="70"
                                max="100"
                            />
                            <div className="d-flex justify-content-between text-muted small">
                                <span>70% (More auto-approvals)</span>
                                <span>100% (Review all)</span>
                            </div>
                        </div>

                        <h6 className="mt-4">Tag Approval</h6>
                        <p className="text-muted small">
                            Tags below {settings.confidenceThreshold}% confidence require manual review.
                            New tags not in the schema always require review.
                        </p>
                    </Col>
                </Row>

                <hr />

                <div className="d-flex justify-content-end">
                    <button
                        className="btn btn-primary"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : 'Save Settings'}
                    </button>
                </div>
            </Card.Body>
        </Card>
    );
};

export default AIEnrichmentHub;
